import { getAccessToken } from "../../utils/auth";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import {
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from "@mui/material";
import axios from "../../utils/api";

const ModelImageDisplay = () => {
  const [modelImages, setModelImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [model, setModel] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const fetchModelImages = useCallback(async () => {
    try {
      const token = getAccessToken();
      const response = await axios.get("/getModelImages", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setModelImages(response.data);
    } catch (error) {
      console.error("Error fetching the model images:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModelImages();
  }, [fetchModelImages]);

  const resetUploadForm = () => {
    setModel("");
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setMessage("");
    setUploading(false);
  };

  const handleOpenUpload = () => {
    resetUploadForm();
    setUploadOpen(true);
  };

  const handleCloseUpload = () => {
    setUploadOpen(false);
    resetUploadForm();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (preview) URL.revokeObjectURL(preview);
    setFile(selectedFile || null);
    setPreview(selectedFile ? URL.createObjectURL(selectedFile) : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!model || !file) {
      setMessage("Please enter model name and select an image.");
      return;
    }

    const formData = new FormData();
    formData.append("model", model);
    formData.append("image", file);

    setUploading(true);
    setMessage("");

    try {
      await axios.post("/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });

      setMessage("Image uploaded successfully!");
      await fetchModelImages();
      setTimeout(() => {
        handleCloseUpload();
      }, 800);
    } catch (error) {
      console.error("Error uploading image:", error);
      setMessage("Failed to upload image.");
      setUploading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "0.75rem 1rem",
    fontSize: "1rem",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    outline: "none",
    marginBottom: 16,
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          overflow: "hidden",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#667eea" }} />
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>Loading</span>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        background:
          "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        padding: "20px 32px 48px",
        boxSizing: "border-box",
      }}
    >
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <Button
        variant="contained"
        startIcon={<CloudUploadIcon />}
        onClick={handleOpenUpload}
        sx={{
          position: "absolute",
          top: 24,
          right: 24,
          zIndex: 10,
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 2,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
          "&:hover": {
            background: "linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)",
          },
        }}
      >
        Upload Image
      </Button>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 32 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Model Images
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View and upload model images to AWS
        </p>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          justifyContent: "center",
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        {modelImages.length > 0 ? (
          modelImages.map((item) => (
            <div
              key={item._id}
              style={{
                width: 220,
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 16,
                textAlign: "center",
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "#1e293b",
                }}
              >
                {item.model}
              </h3>
              <img
                src={item.image}
                alt={item.model}
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: 8,
                  display: "block",
                }}
              />
            </div>
          ))
        ) : (
          <p
            style={{
              width: "100%",
              textAlign: "center",
              color: "#64748b",
              fontSize: "1rem",
              margin: 0,
            }}
          >
            No models found.
          </p>
        )}
      </div>

      <Dialog
        open={uploadOpen}
        onClose={handleCloseUpload}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 700,
            pr: 1,
          }}
        >
          Upload Model Image
          <IconButton onClick={handleCloseUpload} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column" }}>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#64748b",
              }}
            >
              Model name
            </label>
            <input
              type="text"
              placeholder="Enter model name"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={inputStyle}
            />

            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#64748b",
              }}
            >
              Image file
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{
                marginBottom: 16,
                fontSize: 14,
                color: "#64748b",
              }}
            />

            {preview && (
              <img
                src={preview}
                alt="Preview"
                style={{
                  width: "100%",
                  maxHeight: 240,
                  objectFit: "contain",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  marginBottom: 16,
                }}
              />
            )}

            <button
              type="submit"
              disabled={uploading}
              style={{
                width: "100%",
                padding: "0.75rem 1rem",
                fontSize: "1rem",
                fontWeight: 600,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: uploading ? "not-allowed" : "pointer",
                opacity: uploading ? 0.7 : 1,
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
              }}
            >
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </form>

          {message && (
            <p
              style={{
                marginTop: 16,
                marginBottom: 0,
                fontSize: "0.9rem",
                fontWeight: 500,
                color: message.includes("success") ? "#16a34a" : "#dc2626",
              }}
            >
              {message}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModelImageDisplay;
